import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShareLinkBlock } from "@/components/ShareLinkBlock";
import { Loader2, Send, MapPin, Share2, MessageSquare } from "lucide-react";

const STATUS_LABEL = { new: "Nuevo", contacted: "Contactado", qualified: "Calificado", won: "Ganado", lost: "Perdido" };

export default function LeadDetailSheet({ lead, open, onOpenChange, onChanged }) {
  const [busy, setBusy] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [guides, setGuides] = useState([]);
  const [guideId, setGuideId] = useState("");
  const [share, setShare] = useState(null);
  const [smsBusy, setSmsBusy] = useState(false);
  const [smsResult, setSmsResult] = useState(null);
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentText, setConsentText] = useState("");

  useEffect(() => {
    if (open) {
      api.get("/guides").then((r) => setGuides(r.data.filter((g) => g.status === "published"))).catch(() => {});
      setShare(null);
      setGuideId("");
      setSmsResult(null);
    }
  }, [open, lead?.id]);

  if (!lead) return null;

  const changeStatus = async (status) => {
    setBusy("status");
    try {
      const res = await api.put(`/leads/${lead.id}/status`, { status });
      onChanged(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cambiar el estado");
    } finally {
      setBusy(null);
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setBusy("note");
    try {
      const res = await api.post(`/leads/${lead.id}/notes`, { text: noteText });
      onChanged(res.data);
      setNoteText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo agregar la nota");
    } finally {
      setBusy(null);
    }
  };

  const sendGuide = async () => {
    if (!guideId) return toast.error("Elegí una guía");
    setBusy("send");
    try {
      const res = await api.post(`/guides/${guideId}/send`, { lead_id: lead.id });
      setShare(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar el link");
    } finally {
      setBusy(null);
    }
  };

  const sendSms = async (consentMethod) => {
    if (!guideId) return toast.error("Elegí una guía");
    setSmsBusy(true);
    try {
      const res = await api.post(`/guides/${guideId}/send`, {
        lead_id: lead.id, send_sms: true, ...(consentMethod ? { consent_method: consentMethod } : {}),
      });
      setSmsResult(res.data.sms);
      setConsentOpen(false);
      toast.success('SMS encolado ("queued" todavía no significa entregado)');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "consent_required") {
        setConsentText(`Autorizó por teléfono durante la llamada del ${new Date().toLocaleDateString("es-AR")}`);
        setConsentOpen(true);
      } else {
        const message = (detail && typeof detail === "object" ? detail.message : detail) || "No se pudo enviar el SMS";
        if (typeof message === "string" && message.includes("30034")) {
          toast.error("Twilio rechazó el envío y cortó el registro A2P de este cliente. Revisá la tarjeta de Twilio en Conexiones.");
        } else {
          toast.error(message);
        }
      }
    } finally {
      setSmsBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="lead-detail-sheet">
        <SheetHeader>
          <SheetTitle className="font-heading text-left">{lead.name}</SheetTitle>
        </SheetHeader>

        <div className="space-y-5 mt-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-[#71717A]">Teléfono:</span> {lead.phone}</div>
            <div><span className="text-[#71717A]">Email:</span> {lead.email || "-"}</div>
            <div className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-[#71717A]" /> {lead.zip_code || "-"}</div>
            <div><span className="text-[#71717A]">Servicio:</span> {lead.service_requested || "-"}</div>
          </div>
          {lead.message && <p className="text-sm text-[#27272A] bg-[#FAFAFA] border border-[#E4E4E7] rounded-md p-3">{lead.message}</p>}

          <div>
            <h4 className="text-xs uppercase tracking-wider text-[#71717A] font-medium mb-2">Atribución</h4>
            <div className="flex flex-wrap gap-2 text-xs">
              {lead.attribution?.platform && <Badge variant="outline" className="rounded-sm capitalize">{lead.attribution.platform}</Badge>}
              <Badge variant="outline" className="rounded-sm">{lead.attribution?.destination_type || "external"}</Badge>
              {lead.attribution?.tracking_link_code && <Badge variant="outline" className="rounded-sm font-mono">{lead.attribution.tracking_link_code}</Badge>}
              {!lead.assigned_user_id && <Badge className="bg-[#F59E0B]/10 text-[#92400E] border-0 rounded-sm">Sin asignar</Badge>}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs text-[#71717A]">Estado</label>
            <Select value={lead.status} onValueChange={changeStatus} disabled={busy === "status"}>
              <SelectTrigger data-testid="lead-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <Separator />

          <div>
            <h4 className="text-xs uppercase tracking-wider text-[#71717A] font-medium mb-2 flex items-center gap-1">
              <Share2 className="h-3.5 w-3.5" /> Enviar guía
            </h4>
            <div className="flex gap-2">
              <Select value={guideId} onValueChange={setGuideId}>
                <SelectTrigger data-testid="lead-send-guide-select" className="flex-1"><SelectValue placeholder="Elegí una guía publicada" /></SelectTrigger>
                <SelectContent>
                  {guides.map((g) => <SelectItem key={g.id} value={g.id}>{g.title || g.topic}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" onClick={sendGuide} disabled={busy === "send" || !guideId} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md" data-testid="lead-send-guide-button">
                {busy === "send" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              </Button>
              <Button
                size="sm" variant="outline" onClick={() => sendSms()} disabled={smsBusy || !guideId}
                className="rounded-md" data-testid="lead-send-sms-button"
              >
                {smsBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageSquare className="h-3.5 w-3.5" />}
              </Button>
            </div>
            {guides.length === 0 && <p className="text-xs text-[#A1A1AA] mt-1">No hay guías publicadas todavía.</p>}
            {lead.phone_sms_invalid && (
              <p className="text-xs text-[#EF4444] mt-1" data-testid="lead-phone-invalid-warning">
                Twilio marcó este número como inválido para SMS.
              </p>
            )}
            {smsResult && (
              <p className="text-xs text-[#71717A] mt-1" data-testid="lead-sms-result">
                SMS en estado "{smsResult.status}"
                {smsResult.status !== "delivered" && " (todavía no confirma entrega)"}.
              </p>
            )}
            {share && <div className="mt-3"><ShareLinkBlock share={share} message={`Hi ${lead.name}! Here's the guide you asked about: ${share.short_url}`} /></div>}
          </div>

          <Dialog open={consentOpen} onOpenChange={setConsentOpen}>
            <DialogContent data-testid="consent-dialog">
              <DialogHeader><DialogTitle className="font-heading">Registrar consentimiento</DialogTitle></DialogHeader>
              <p className="text-xs text-[#71717A]">
                No hay consentimiento registrado para {lead.phone}. Describí cómo y cuándo el cliente
                autorizó recibir SMS -- Twilio exige poder mostrar la fecha y el método.
              </p>
              <Textarea
                rows={3} value={consentText} onChange={(e) => setConsentText(e.target.value)}
                data-testid="consent-method-input"
              />
              <Button
                onClick={() => sendSms(consentText)} disabled={smsBusy || !consentText.trim()}
                className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md" data-testid="consent-confirm-button"
              >
                {smsBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                Confirmar y enviar
              </Button>
            </DialogContent>
          </Dialog>

          <Separator />

          <div>
            <h4 className="text-xs uppercase tracking-wider text-[#71717A] font-medium mb-2">Notas</h4>
            <div className="space-y-2 mb-3" data-testid="lead-notes-list">
              {(lead.notes || []).length === 0 && <p className="text-xs text-[#A1A1AA]">Sin notas todavía.</p>}
              {(lead.notes || []).map((n, i) => (
                <div key={i} className="text-xs border border-[#E4E4E7] rounded-md p-2.5">
                  <p>{n.text}</p>
                  <p className="text-[#A1A1AA] mt-1">{n.author_name} · {new Date(n.created_at).toLocaleString("es-AR")}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Textarea rows={2} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Agregar una nota..." data-testid="lead-note-input" className="flex-1" />
              <Button size="sm" onClick={addNote} disabled={busy === "note"} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md self-end" data-testid="lead-add-note-button">
                {busy === "note" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Guardar"}
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
